import { Box, Button, Flex, Text, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import * as _ from 'lodash';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { createExtendedResponseTape } from '@finley-technologies/calc-agent';
import { csvConversion, finleyAssert } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { AssignmentReceivableGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

import { assignmentReceivablesToBorrowingBaseReceivables } from './utils';
import { colors } from 'packages/common-frontend/colors';
import {
  errorToastConfig,
  successToastConfig,
} from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useGetCurrentCreditAgreementFullArtifactQuery } from 'packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { parseBorrowingBaseMechanics } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/utils';
import {
  useCreateAssignmentServicerReportPreviewV1Mutation,
  useGetLatestCompletedReceivableResponseTapePreviewIdsQuery,
} from 'packages/loanProduct/requests/requests.gql.generated';
import {
  EmptyDownloadAssignmentPreviewButton,
  LoadingEmailServicerReportPreviewButton,
} from 'packages/loanProduct/ui/DownloadPreviewLoadingButtons';
import { FiMail } from 'react-icons/fi';

export const EmailServicerReportPreviewButton = ({
  creditFacilityId,
  proposedReceivables,
  previouslyAssignedReceivables,
  fileVersion,
  reportingDate,
  buttonLabel,
  fileNamePrefix,
}: {
  creditFacilityId: string;
  proposedReceivables: AssignmentReceivableGQLType[];
  previouslyAssignedReceivables: AssignmentReceivableGQLType[];
  fileVersion: number;
  reportingDate: Util.FullDateStr;
  buttonLabel: string;
  fileNamePrefix: string;
}) => {
  const toast = useToast();
  const navigate = useNavigate();

  const {
    data: creditAgreementData,
    loading: creditAgreementLoading,
  } = useGetCurrentCreditAgreementFullArtifactQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: { creditFacilityId },
  });

  const {
    data: latestCompletedPreviewId,
    loading: latestCompletedPreviewIdIsLoading,
  } = useGetLatestCompletedReceivableResponseTapePreviewIdsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityIds: [creditFacilityId],
      reportingDate,
      fileVersion,
    },
  });

  const [
    createAssignmentServicerReportPreview,
  ] = useCreateAssignmentServicerReportPreviewV1Mutation({
    onCompleted: () => {
      successToastConfig.title =
        'Creating servicing report preview from assignment proposals, please check your email in a few minutes.';
      toast(successToastConfig);
    },
    onError: () => {
      errorToastConfig.title = `Error creating servicing report preview from assignment proposals.`;
      toast(errorToastConfig);
    },
  });

  if (
    creditAgreementLoading ||
    !creditAgreementData ||
    !creditAgreementData.currentCreditAgreementFullArtifact ||
    latestCompletedPreviewIdIsLoading ||
    !latestCompletedPreviewId ||
    !latestCompletedPreviewId.latestCompletedReceivableResponseTapePreviewIds
  ) {
    return <LoadingEmailServicerReportPreviewButton />;
  }

  const allReceivablesForDownload = _.compact(
    _.concat(previouslyAssignedReceivables, proposedReceivables),
  );

  if (_.isEmpty(allReceivablesForDownload)) {
    return <EmptyDownloadAssignmentPreviewButton buttonLabel={buttonLabel} />;
  }

  const {
    currentCreditAgreementFullArtifact: { creditAgreement, id },
  } = creditAgreementData;

  const receivableResponseTapePreviewId =
    latestCompletedPreviewId.latestCompletedReceivableResponseTapePreviewIds[0];

  let parsedCreditAgreement = null;

  try {
    parsedCreditAgreement = parseCreditAgreementFullArtifact(creditAgreement);
    finleyAssert(
      parsedCreditAgreement,
      `Expect creditAgreementFullArtifact to exist for creditAgreementId ${id}`,
    );

    const {
      loanProductMechanics,
      borrowingBaseMechanics,
    } = parseBorrowingBaseMechanics(parsedCreditAgreement);

    const { multiLoanProductConcentrationLimits } = borrowingBaseMechanics;

    const borrowingBaseReceivablesForAssignedAndProposedReceivables = assignmentReceivablesToBorrowingBaseReceivables(
      {
        assignmentReceivables: allReceivablesForDownload,
        creditFacilityId,
      },
    );
    const borrowingBaseReceivablesForProposedReceivables = !_.isEmpty(
      proposedReceivables,
    )
      ? assignmentReceivablesToBorrowingBaseReceivables({
          assignmentReceivables: proposedReceivables,
          creditFacilityId,
        })
      : undefined;

    const { facilityType } = parsedCreditAgreement;

    const createAndEmailServicerReportPreviewCallback = async () => {
      const { extendedResponseTape } = await createExtendedResponseTape({
        facilityType,
        multiLoanProductConcentrationLimits,
        loanProductMechanics,
        borrowingBaseReceivables: borrowingBaseReceivablesForAssignedAndProposedReceivables,
      });

      const proposedExtendedResponseTape = borrowingBaseReceivablesForProposedReceivables
        ? (
            await createExtendedResponseTape({
              facilityType,
              multiLoanProductConcentrationLimits,
              loanProductMechanics,
              borrowingBaseReceivables: borrowingBaseReceivablesForProposedReceivables,
            })
          ).extendedResponseTape
        : undefined;

      createAssignmentServicerReportPreview({
        variables: {
          assignmentServicerReportPreviewInput: {
            creditFacilityId,
            reportingDate,
            fileVersion,
            servicerReportPreviewType: 'assignmentServicerReportPreview',
            receivableResponseTapePreviewId,
            loanProductId: loanProductMechanics.loanProductId,
            extendedResponseTapePreview: csvConversion.arrayToCsvString({
              csvArray: extendedResponseTape,
              header: true,
            }),
            proposedExtendedResponseTapePreview: proposedExtendedResponseTape
              ? csvConversion.arrayToCsvString({
                  csvArray: proposedExtendedResponseTape,
                  header: true,
                })
              : undefined,
          },
        },
      });
    };

    const fileName = `${fileNamePrefix}-${reportingDate}-v${fileVersion}`;

    return (
      <Button
        fontWeight="normal"
        width="100%"
        borderRadius="0"
        borderTop="1px"
        borderColor={colors.gray[200]}
        background="white"
        _hover={{
          background: 'blackAlpha.50',
        }}
        onClick={() => {
          createAndEmailServicerReportPreviewCallback();
          Sentry.addBreadcrumb({
            category: 'ui.click',
            message: `Clicked email servicer report preview from assignment ${fileName}`,
            level: Sentry.Severity.Info,
          });
        }}
      >
        <Flex width="100%" justify="space-between">
          <Text>Email pro forma servicer report preview</Text>
          {/* marginRight to align with accordion button component below */}
          <Box marginRight="2px">
            <FiMail />
          </Box>
        </Flex>
      </Button>
    );
  } catch (err) {
    errorToastConfig.title = `Error parsing credit agreement artifact for credit agreement ${id}`;
    toast(errorToastConfig);
    navigate('/');
  }

  throw new Error('unreachable code here');
};
